/**
 * Action to-do items derived from domain feed events (hard-coded per event kind).
 *
 * Each logical bucket keeps at most one todo, always tied to the newest matching
 * feed root. When a newer event of the same bucket arrives, the previous todo
 * is dropped — reviewed or not — and replaced.
 */
import { plural } from "@/features/feed/insights/formatInsight";
import {
  bookingHref,
  operationalDigestShowMoreHref,
} from "@/features/feed/feedLinks";
import { isBookingOpportunityPayload } from "@/features/feed/FeedBookingOpportunityDetail";
import { isOperationalDigestPayload } from "@/features/feed/FeedOperationalDigestDetail";
import { cutoffBookingId, isCutoffApproachingEvent } from "@/features/feed/feedCutoffGroups";
import { BOOKING_QUEUE_PRESETS } from "@/features/dashboard/home/bookingQueuePresets";
import { buildBookingsFilterUrl } from "@/lib/bookingsFilterLink";
import {
  applyViewerScopeToBookings,
  liveViewerScope,
} from "@/lib/viewerScopeFilters";
import type {
  BookingOpportunityPayload,
  FeedEvent,
  OperationalDigestDimension,
  OperationalDigestPayload,
} from "@/types/feed";

export const FEED_TODO_LIMIT = 7;

/** When the rail already shows {@link FEED_TODO_LIMIT} items, drop a reviewed row after this delay. */
export const FEED_TODO_REVIEWED_REMOVE_MS = 10_000;

/**
 * Stable bucket key — a new feed event with the same bucket replaces the todo.
 *
 * Rebook / first-book opportunities are **per booking** (one todo per booking,
 * keyed by its `source_id`) so each booking is reviewed on its own and a newer
 * opportunity for the same booking supersedes the older card; the operational /
 * cutoff buckets stay aggregated.
 */
export type FeedTodoBucket =
  | `opportunity:${string}`
  | `operational:${OperationalDigestDimension}`
  | "alert:cutoff_approaching";

export interface FeedTodoCandidate {
  bucket: FeedTodoBucket;
  /** Newest feed event id backing this todo (used for review + reset detection). */
  eventId: string;
  label: string;
  href: string;
  /** RFC3339 from the source event — drives ordering and the max-7 cap. */
  createdAt: string;
  /** Server-side review flag on the source event, when still present in the feed list. */
  isReviewed: boolean;
}

function formatSourceIdSample(ids: string[], max = 4): string {
  const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
  if (unique.length === 0) return "";
  const shown = unique.slice(0, max);
  const rest = unique.length - shown.length;
  const body = shown.join(", ");
  return rest > 0 ? `${body} +${rest}` : body;
}

function bookingOpportunityTodo(
  event: FeedEvent,
  payload: BookingOpportunityPayload,
): FeedTodoCandidate | null {
  const dimension = payload.dimension;
  if (dimension !== "rebook_opportunity" && dimension !== "first_book") {
    return null;
  }
  const sourceId = (payload.source_id ?? payload.booking?.source_id)?.trim();
  if (!sourceId) return null;

  const label =
    dimension === "rebook_opportunity"
      ? `Review rebook #${sourceId}`
      : `Review first book #${sourceId}`;

  return {
    // One todo per booking: keyed by `source_id` so a newer rebook/first-book
    // opportunity for the same booking (later search cycle) supersedes the older
    // card instead of stacking a duplicate action item. Cross-dimension by
    // design — a booking has at most one open opportunity todo.
    bucket: `opportunity:${sourceId}`,
    eventId: event.id,
    label,
    href: bookingHref(sourceId),
    createdAt: event.created_at,
    isReviewed: event.is_reviewed === true,
  };
}

const OPERATIONAL_TODO_DIMENSIONS = new Set<OperationalDigestDimension>([
  "stuffing",
  "cutoff",
  "departing",
  "delayed",
]);

const OPERATIONAL_DIMENSION_LABEL: Partial<
  Record<OperationalDigestDimension, string>
> = {
  stuffing: "stuffing",
  cutoff: "cutoff",
  departing: "ETD / departing",
  delayed: "delayed",
};

function operationalDigestTodo(
  event: FeedEvent,
  payload: OperationalDigestPayload,
): FeedTodoCandidate | null {
  const dimension = payload.dimension;
  if (!OPERATIONAL_TODO_DIMENSIONS.has(dimension)) return null;

  const count = payload.booking_count;
  const ids = payload.bookings.map((b) => b.source_id);
  const sample = formatSourceIdSample(ids);
  const dimLabel = OPERATIONAL_DIMENSION_LABEL[dimension] ?? dimension;
  const label = sample
    ? `Review ${plural(count, `${dimLabel} booking`)} (${sample})`
    : `Review ${plural(count, `${dimLabel} booking`)}`;

  return {
    bucket: `operational:${dimension}`,
    eventId: event.id,
    label,
    href: operationalDigestShowMoreHref(
      payload.show_more,
      payload.scope,
      event.entity_scope?.booking_ids,
    ),
    createdAt: event.created_at,
    isReviewed: event.is_reviewed === true,
  };
}

function cutoffApproachingAggregate(
  events: FeedEvent[],
): FeedTodoCandidate | null {
  const cutoffEvents = events.filter(isCutoffApproachingEvent);
  if (cutoffEvents.length === 0) return null;

  const sorted = [...cutoffEvents].sort(
    (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
  );
  const head = sorted[0];
  const ids = sorted
    .map((e) => cutoffBookingId(e))
    .filter((id): id is string => !!id);
  const sample = formatSourceIdSample(ids);
  const count = ids.length;
  const filters = applyViewerScopeToBookings(
    BOOKING_QUEUE_PRESETS.cutoff_soon.resolveFilters(),
    liveViewerScope(),
  );

  const label = sample
    ? `Review ${plural(count, "cargo cutoff")} (${sample})`
    : `Review ${plural(count, "cargo cutoff")}`;

  return {
    bucket: "alert:cutoff_approaching",
    eventId: head.id,
    label,
    href: buildBookingsFilterUrl(filters),
    createdAt: head.created_at,
    isReviewed: sorted.every((e) => e.is_reviewed === true),
  };
}

/** Try to derive a todo from a single feed root (not cutoff aggregate). */
export function deriveFeedTodoFromEvent(
  event: FeedEvent,
  opts?: { isClient?: boolean },
): FeedTodoCandidate | null {
  if (opts?.isClient && isBookingOpportunityPayload(event.payload)) {
    return null;
  }
  if (isBookingOpportunityPayload(event.payload)) {
    return bookingOpportunityTodo(event, event.payload);
  }
  if (
    event.event_type === "operational_digest" &&
    isOperationalDigestPayload(event.payload)
  ) {
    return operationalDigestTodo(event, event.payload);
  }
  return null;
}

/**
 * Build the current feed todo list: newest event wins per bucket, then cap at
 * {@link FEED_TODO_LIMIT} by event recency.
 */
export function buildFeedTodoCandidates(
  events: FeedEvent[],
  opts?: { isClient?: boolean },
): FeedTodoCandidate[] {
  const byBucket = new Map<FeedTodoBucket, FeedTodoCandidate>();

  for (const event of events) {
    const candidate = deriveFeedTodoFromEvent(event, opts);
    if (!candidate) continue;
    const existing = byBucket.get(candidate.bucket);
    if (
      !existing ||
      Date.parse(candidate.createdAt) >= Date.parse(existing.createdAt)
    ) {
      byBucket.set(candidate.bucket, candidate);
    }
  }

  const cutoff = cutoffApproachingAggregate(events);
  if (cutoff) {
    const existing = byBucket.get(cutoff.bucket);
    if (
      !existing ||
      Date.parse(cutoff.createdAt) >= Date.parse(existing.createdAt)
    ) {
      byBucket.set(cutoff.bucket, cutoff);
    }
  }

  return [...byBucket.values()]
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, FEED_TODO_LIMIT);
}
